import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { CoachMarkService } from "../coach-mark.service";
import { FeatureFlagService } from "../feature-flag.service";
import { InteractionTrackingService } from "../interaction-tracking.service";
import { NovaCompanionSettingsService } from "../nova-companion-settings.service";
import { OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { LearnOrientationLesson } from "./learn-orientation-lesson";

/**
 * The shared shape of the Nova first-use lessons that point at something inside the open Nova chat
 * (FLIP-384: N03A the microphone, N03B Voice Chat, N04 the coloured header) - informational anchored
 * prompts without buttons, on the flow LearnOrientationLesson provides.
 *
 * - The host is the chat sheet, a dialog on top of whatever route the user is on: the prompt shows
 *   over it as soon as the host is ready (`overDialogs`), and the lesson is on-surface for as long
 *   as the chat is mounted. The chat calls enter() when it opens and leave() when it closes.
 * - Not shown while Nova Companion is disabled (chat lessons are suppressed then).
 * - Nova is never opened by a lesson: these only exist while the user already has it open.
 * - They run as a sequence once N02 has been closed: N03A, then N03B, then N04. The chat drives each
 *   with setReady(), starting a lesson only once the one before it is `done$` - so they follow one
 *   another automatically, never two prompts at once.
 */
@Injectable()
export abstract class NovaChatLesson extends LearnOrientationLesson {
  private companionEnabled = true;

  constructor(
    router: Router,
    coachMarkService: CoachMarkService,
    blockingUiSignal: BlockingUiSignalService,
    featureFlagService: FeatureFlagService,
    paymentsService: PaymentsService,
    onboardingStepService: OnboardingStepService,
    interactionTracking: InteractionTrackingService,
    companionSettings: NovaCompanionSettingsService
  ) {
    super(
      router,
      coachMarkService,
      blockingUiSignal,
      featureFlagService,
      paymentsService,
      onboardingStepService,
      interactionTracking
    );
    companionSettings.enabled$.subscribe((enabled) => (this.companionEnabled = enabled));
  }

  protected get overDialogs(): boolean {
    return true;
  }

  protected isOnSurface(): boolean {
    return true;
  }

  /** Whether the lesson may show at all right now (Companion enabled). */
  protected get companionOn(): boolean {
    return this.companionEnabled;
  }
}
